import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import { useUserStore } from '../stores/useUserStore';
import { configs } from '../configs';
import { authenticatedApiCall } from '../utils/authenticatedFetch';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Star,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  TrendingUp,
  Users,
  Calendar,
  Award,
  CheckCircle,
  Clock,
  Filter,
} from 'lucide-react';

interface RatingData {
  id: string;
  staffId: string;
  staffName: string;
  customerId: string;
  customerName: string;
  rating: number;
  comment: string;
  category: string;
  date: string;
  isVerified: boolean;
  verifiedBy?: string;
  verifiedAt?: string;
}

interface RatingStats {
  averageRating: number;
  totalRatings: number;
  fiveStar: number;
  fourStar: number;
  threeStar: number;
  twoStar: number;
  oneStar: number;
  recentRatings: number;
  verifiedRatings: number;
}

const ModernStaffRating: React.FC = () => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const { user } = useUserStore();

  const [ratings, setRatings] = useState<RatingData[]>([]);
  const [stats, setStats] = useState<RatingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  useEffect(() => {
    loadRatings();
    loadStats();
  }, [filter, sortBy]);

  const loadRatings = async () => {
    setLoading(true);
    try {
      // Fetch ratings from API with authentication
      const apiRatings = await authenticatedApiCall<any[]>(
        `${configs.apiRoot}/staff_ratings`
      );

      // Transform API data to component format
      const ratings: RatingData[] = apiRatings.map((rating: any) => ({
        id: rating._id,
        staffId: rating.staffId,
        staffName: rating.staffName || 'You',
        customerId: rating.customerId,
        customerName: rating.customerName,
        rating: rating.rating,
        comment: rating.comment,
        category: rating.category,
        date: rating.date,
        isVerified: rating.isVerified,
        verifiedBy: rating.verifiedBy,
        verifiedAt: rating.verifiedAt,
      }));

      let filteredRatings = ratings;

      // Apply filters
      if (filter === 'verified') {
        filteredRatings = filteredRatings.filter((r) => r.isVerified);
      } else if (filter === 'unverified') {
        filteredRatings = filteredRatings.filter((r) => !r.isVerified);
      } else if (filter === 'high') {
        filteredRatings = filteredRatings.filter((r) => r.rating >= 4);
      } else if (filter === 'low') {
        filteredRatings = filteredRatings.filter((r) => r.rating <= 2);
      }

      // Apply sorting
      if (sortBy === 'date') {
        filteredRatings.sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );
      } else if (sortBy === 'rating') {
        filteredRatings.sort((a, b) => b.rating - a.rating);
      } else if (sortBy === 'category') {
        filteredRatings.sort((a, b) => a.category.localeCompare(b.category));
      }

      setRatings(filteredRatings);
    } catch (error) {
      console.error('Error loading ratings:', error);
      notify(
        translate('pos.staff.rating.failed_load', 'Failed to load ratings'),
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // Mock stats - replace with actual API call
      const mockStats: RatingStats = {
        averageRating: 4.4,
        totalRatings: 25,
        fiveStar: 15,
        fourStar: 6,
        threeStar: 3,
        twoStar: 1,
        oneStar: 0,
        recentRatings: 5,
        verifiedRatings: 18,
      };

      setStats(mockStats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 4) return 'text-green-600';
    if (rating >= 3) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRatingStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Customer Service':
        return <Users className="h-4 w-4" />;
      case 'Speed':
        return <Clock className="h-4 w-4" />;
      case 'Product Knowledge':
        return <Award className="h-4 w-4" />;
      default:
        return <Star className="h-4 w-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>
            {translate('pos.staff.rating.loading', 'Loading ratings...')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-6">
      <div className="flex flex-col items-start">
        <h1 className="text-3xl font-bold text-foreground">
          {translate('pos.staff.rating.title', 'Your Ratings')}
        </h1>
        <p className="text-muted-foreground mt-2">
          {translate(
            'pos.staff.rating.subtitle',
            'Customer feedback and ratings for your performance'
          )}
        </p>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Star className="h-4 w-4" />
                {translate('pos.staff.rating.average_rating', 'Average Rating')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-primary">
                {stats.averageRating.toFixed(1)}
              </div>
              <div className="flex items-center gap-1 mt-1">
                {getRatingStars(Math.round(stats.averageRating))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                {translate('pos.staff.rating.total_ratings', 'Total Ratings')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalRatings}</div>
              <div className="text-sm text-muted-foreground">
                {stats.recentRatings}{' '}
                {translate('pos.staff.rating.this_week', 'this week')}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                {translate('pos.staff.rating.verified', 'Verified')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {stats.verifiedRatings}
              </div>
              <div className="text-sm text-muted-foreground">
                {Math.round((stats.verifiedRatings / stats.totalRatings) * 100)}
                {translate('pos.staff.rating.verified_percent', '% verified')}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                {translate('pos.staff.rating.high_ratings', 'High Ratings')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {stats.fiveStar + stats.fourStar}
              </div>
              <div className="text-sm text-muted-foreground">
                {translate(
                  'pos.staff.rating.high_ratings_desc',
                  '4+ star ratings'
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Rating Distribution */}
      {stats && (
        <Card>
          <CardHeader>
            <CardTitle>
              {translate(
                'pos.staff.rating.rating_distribution',
                'Rating Distribution'
              )}
            </CardTitle>
            <CardDescription>
              {translate(
                'pos.staff.rating.rating_distribution_desc',
                'Breakdown of all your customer ratings'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { stars: 5, count: stats.fiveStar, color: 'bg-green-500' },
                { stars: 4, count: stats.fourStar, color: 'bg-blue-500' },
                { stars: 3, count: stats.threeStar, color: 'bg-yellow-500' },
                { stars: 2, count: stats.twoStar, color: 'bg-orange-500' },
                { stars: 1, count: stats.oneStar, color: 'bg-red-500' },
              ].map(({ stars, count, color }) => (
                <div key={stars} className="flex items-center gap-3">
                  <div className="flex items-center gap-1 w-16">
                    <span className="text-sm font-medium">{stars}</span>
                    <Star className="h-3 w-3 text-yellow-400" />
                  </div>
                  <div className="flex-1">
                    <Progress
                      value={(count / stats.totalRatings) * 100}
                      className="h-2"
                    />
                  </div>
                  <div className="text-sm font-medium w-8 text-right">
                    {count}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters and Sorting */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            {translate('pos.staff.rating.filters_sorting', 'Filters & Sorting')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div>
              <Label htmlFor="filter">
                {translate('pos.staff.rating.filter_by', 'Filter by:')}
              </Label>
              <select
                id="filter"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="ml-2 px-3 py-1 border rounded"
              >
                <option value="all">
                  {translate('pos.staff.rating.all_ratings', 'All Ratings')}
                </option>
                <option value="verified">
                  {translate(
                    'pos.staff.rating.verified_only',
                    'Verified Only'
                  )}
                </option>
                <option value="unverified">
                  {translate(
                    'pos.staff.rating.unverified_only',
                    'Unverified Only'
                  )}
                </option>
                <option value="high">
                  {translate(
                    'pos.staff.rating.high_ratings_filter',
                    'High Ratings (4+)'
                  )}
                </option>
                <option value="low">
                  {translate(
                    'pos.staff.rating.low_ratings_filter',
                    'Low Ratings (2-)'
                  )}
                </option>
              </select>
            </div>

            <div>
              <Label htmlFor="sort">
                {translate('pos.staff.rating.sort_by', 'Sort by:')}
              </Label>
              <select
                id="sort"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="ml-2 px-3 py-1 border rounded"
              >
                <option value="date">
                  {translate('pos.staff.rating.sort_date', 'Date (Newest)')}
                </option>
                <option value="rating">
                  {translate('pos.staff.rating.sort_rating', 'Rating (Highest)')}
                </option>
                <option value="category">
                  {translate('pos.staff.rating.sort_category', 'Category')}
                </option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ratings List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            {translate('pos.staff.rating.customer_ratings', 'Customer Ratings')}
          </CardTitle>
          <CardDescription>
            {translate(
              'pos.staff.rating.customer_ratings_desc',
              'Recent customer feedback and ratings'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {ratings.map((rating) => (
              <div
                key={rating.id}
                className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      {getRatingStars(rating.rating)}
                    </div>
                    <div>
                      <div className="font-semibold">{rating.customerName}</div>
                      <div className="text-sm text-muted-foreground">
                        {formatDate(rating.date)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className="flex items-center gap-1"
                    >
                      {getCategoryIcon(rating.category)}
                      {rating.category === 'Customer Service'
                        ? translate(
                            'pos.staff.rating.category_customer_service',
                            'Customer Service'
                          )
                        : rating.category === 'Speed'
                        ? translate('pos.staff.rating.category_speed', 'Speed')
                        : rating.category === 'Product Knowledge'
                        ? translate(
                            'pos.staff.rating.category_product_knowledge',
                            'Product Knowledge'
                          )
                        : rating.category}
                    </Badge>
                    {rating.isVerified && (
                      <Badge
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        <CheckCircle className="h-3 w-3" />
                        {translate('pos.staff.rating.verified', 'Verified')}
                      </Badge>
                    )}
                  </div>
                </div>

                {rating.comment && (
                  <div className="bg-muted/50 p-3 rounded-md">
                    <p className="text-sm">{rating.comment}</p>
                  </div>
                )}

                {rating.isVerified && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    {translate(
                      'pos.staff.rating.verified_by_manager',
                      'Verified by manager on'
                    )}{' '}
                    {formatDate(rating.verifiedAt || '')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ModernStaffRating;
